"use client";

import { createContext, useCallback, useContext, useState } from "react";
import { create } from "zustand";
import { useSearchParams } from "next/navigation";
import { SortOrder } from "@/01-core/entity/entities/viewDetail";
import { TimeGranularity } from "@/01-core/entity/entities/entity";
import { BEIJING_NOW_DATE_FUNC, CountryCode, DEFAULT_START_DATE_FUNC, getInitialCountryCode } from "@/00-shared/const";
import { addMonths, format, startOfMonth, subDays } from "date-fns";
import { TIME_JOINT_LABEL } from "./entity/CustomizedDatePickerV2";
const getCurrentMonthFirstDay = () => {
  return startOfMonth(BEIJING_NOW_DATE_FUNC());
};
const disabledResetKeyList = ["tiktok_product_new_on_shelf_time"];
export enum DisplayType {
  List = "0",
  Card = "1",
}
export interface Filter {
  filterKey: string;
  value: string;
}
export interface Sorter {
  fieldKey: string;
  sortOrder: SortOrder;
}
export enum FrontEndRenderType {
  Custom = "custom",
  Number = "number",
  Currency = "currency",
  Text = "text",
}
export interface FieldMap {
  [key: string]: string;
}
export interface HeaderGroupConfig {
  i18nKey: string;
  groupKey: string;
  fieldMap: FieldMap;
  renderType: FrontEndRenderType;
}
export interface HeaderConfig {
  headerGroupList: HeaderGroupConfig[];
  defaultSorter: {
    fleldKey: string;
    sortOrder: SortOrder;
  }[];
  sorterList: string[];
  apiPath?: string;
  version: number;
}
export type OptionList = {
  option_name: string;
  children?: OptionList[];
};
export type FilterOptions = {
  filter_key: string;
  filter_type: string;
  quick_option_list: OptionList[];
};
export type TimeFilter = {
  granularity: TimeGranularity;
  start_date_time: string;
};
interface TableStore {
  filters: Filter[];
  pageIndex: number;
  keyword: string;
  sorter?: Sorter[];
  displayType: DisplayType;
  setDisplayType: (d: DisplayType) => void;
  timeFilter: TimeFilter | undefined;
  setPageIndex: (pageIndex: number) => void;
  updateFilter: (key: string, value: string) => void;
  setKeyword: (keyword: string) => void;
  setSorter: (sorter: Sorter[]) => void;
  setTimeFilter: (timeFilter: TimeFilter) => void;
  resetKeywordAndFilters: () => void;
  stateHistory: TableState[];
  backToPrevState: () => void;
}
type TableState = Pick<TableStore, "filters" | "pageIndex" | "keyword" | "timeFilter" | "sorter" | "displayType">;
const createTableStore = ({
  displayType,
  pageIndex,
  filters,
  sorter,
  timeFilter,
  keyword,
  afterUpdate
}: {
  displayType: DisplayType;
  pageIndex: number;
  filters: Filter[];
  sorter: Sorter[];
  timeFilter?: TimeFilter;
  keyword: string;
  afterUpdate: (store: TableStore) => void;
}) => create<TableStore>((set, get) => ({
  keyword,
  filters: filters,
  sorter,
  displayType,
  pageIndex,
  timeFilter,
  stateHistory: [],
  setPageIndex: pageIndex => {
    set(state => {
      const newState = {
        ...state,
        pageIndex
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, state]
      };
    });
    afterUpdate(get());
  },
  setTimeFilter(timeFilter) {
    set(state => {
      const newState = {
        ...state,
        timeFilter
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, newState]
      };
    });
    afterUpdate(get());
  },
  setKeyword: keyword => {
    set(state => {
      const newState = {
        ...state,
        keyword
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, newState]
      };
    });
    afterUpdate(get());
  },
  setSorter: (sorter: Sorter[]) => {
    set(state => {
      const newState = {
        ...state,
        sorter
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, newState]
      };
    });
    afterUpdate(get());
  },
  setDisplayType: (displayType: DisplayType) => {
    set(state => {
      const newState = {
        ...state,
        displayType
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, newState]
      };
    });
    afterUpdate(get());
  },
  updateFilter: (key: string, value: string) => {
    set(state => {
      if (state.filters.find(filter => filter.filterKey === key)) {
        return {
          filters: state.filters.map(filter => filter.filterKey === key ? {
            filterKey: key,
            value
          } : filter),
          stateHistory: [...state.stateHistory, state]
        };
      }
      return {
        filters: [...state.filters, {
          filterKey: key,
          value
        }],
        stateHistory: [...state.stateHistory, state]
      };
    });
    afterUpdate(get());
  },
  resetKeywordAndFilters: () => {
    set(state => {
      const disabledResetFilter = state.filters.filter(filter => disabledResetKeyList.includes(filter.filterKey));
      const newState = {
        ...state,
        keyword: "",
        filters: disabledResetFilter,
        pageIndex: 0
      };
      return {
        ...newState,
        stateHistory: [...state.stateHistory, newState]
      };
    });
    afterUpdate(get());
  },
  backToPrevState() {
    set(state => {
      if (state.stateHistory.length === 0) return state;
      const prevState = state.stateHistory[state.stateHistory.length - 1];
      const newHistory = state.stateHistory.slice(0, -1);
      return {
        ...prevState,
        stateHistory: newHistory
      };
    });
  }
}));
const StoreContext = createContext<ReturnType<typeof createTableStore> | null>(null);
export const useTableStore = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error("useTableStore must be used within a StoreProvider");
  }
  return store;
};
function initStore({
  searchParams,
  tablePrefix,
  defaultFilter,
  defalutSorter,
  filterKeyList,
  defaultDisplayType,
  shouldAddTimeFilter,
  showMonthPicker
}: {
  searchParams: URLSearchParams;
  tablePrefix: string;
  defaultFilter: Filter[];
  filterKeyList: string[];
  defalutSorter: Sorter[];
  shouldAddTimeFilter: boolean;
  defaultDisplayType: DisplayType;
  showMonthPicker: boolean;
}) {
  const defalutTimeFilter = {
    granularity: TimeGranularity.DAILY,
    start_date_time: format(DEFAULT_START_DATE_FUNC(), "yyyyMMdd")
  };
  const defalutMonthTimeFilter = {
    granularity: TimeGranularity.MONTHLY,
    start_date_time: format(getCurrentMonthFirstDay(), "yyyyMMdd")
  };
  const urlParamsObject = Object.fromEntries(searchParams.entries());
  const urlFilter = Object.entries(urlParamsObject).map(([key, value]) => {
    if (filterKeyList.includes(key)) {
      return {
        filterKey: key,
        value: value as string
      };
    }
  }).filter(Boolean) as Filter[];
  const keywordKey = tablePrefix + "keyword";
  const pageIndexKey = tablePrefix + "pageIndex";
  const sorterKey = tablePrefix + "sorter";
  const displayTypeKey = tablePrefix + "displayType";
  const initDisplayType = urlParamsObject[displayTypeKey] ? urlParamsObject[displayTypeKey] as DisplayType : defaultDisplayType;
  const initTimeFilter = urlParamsObject?.granularity && urlParamsObject?.start_date_time ? {
    granularity: Number(urlParamsObject.granularity) as TimeGranularity,
    start_date_time: urlParamsObject.start_date_time
  } : defalutTimeFilter;
  const initPage = urlParamsObject[pageIndexKey] ? Number(urlParamsObject[pageIndexKey]) : 0;
  const initSorter = urlParamsObject[sorterKey] ? JSON.parse(urlParamsObject[sorterKey]) : defalutSorter;
  const initFilter = urlFilter?.length ? urlFilter : defaultFilter.map(filter => {
    if (filter.value === "custom_date_value") {
      return {
        ...filter,
        value: format(subDays(BEIJING_NOW_DATE_FUNC(), 3), "yyyy-MM-dd") + TIME_JOINT_LABEL
      };
    }
    if (filter.value === "custom_last_7_day") {
      return {
        ...filter,
        value: format(subDays(BEIJING_NOW_DATE_FUNC(), 7), "yyyyMMdd") + TIME_JOINT_LABEL
      };
    }
    return filter;
  });
  return {
    pageIndex: initPage,
    filters: initFilter,
    keyword: urlParamsObject[keywordKey] ?? "",
    timeFilter: showMonthPicker ? defalutMonthTimeFilter : shouldAddTimeFilter ? initTimeFilter : undefined,
    sorter: initSorter,
    displayType: initDisplayType
  };
}
export interface FilterConfig {
  tableTitleI18nKey?: string;
  shouldRenderSearchBar?: boolean;
  shouldRenderDatePicker?: boolean;
  apiPath?: string;
  filterGroupList: {
    i18nKey: string;
    filterList: {
      filterKey: string;
      filterType: "checkbox" | "cascader" | "select" | "customSelect" | "time" | "toggle" | "switch" | "timeRange" | "adsTimeRange" | "inputTag" | "toggleTag";
    }[];
  }[];
  defaultFilter: {
    filterKey: string;
    value: string;
  }[];
}
export const TableStoreProvider: React.FC<{
  children: React.ReactNode;
  tablePrefix?: string;
  filterConfig?: FilterConfig;
  defalutSorter?: Sorter[];
  disableUpdateUrl?: boolean;
  defaultDisplayType?: DisplayType;
  showMonthPicker?: boolean;
}> = ({
  children,
  defaultDisplayType = DisplayType.List,
  tablePrefix = "",
  filterConfig,
  defalutSorter,
  disableUpdateUrl,
  showMonthPicker = false
}) => {
  const searchParams = useSearchParams();
  const initCountryCode = getInitialCountryCode();
  const _defaultFilter = filterConfig?.defaultFilter || [];
  const defaultFilter = _defaultFilter.filter(filter => {
    if (filter.filterKey === "tiktok_live_search_is_commercial_live" && initCountryCode === CountryCode.MX) {
      return false;
    }
    return true;
  });
  const updateUrl = useCallback((state: Pick<TableStore, "filters" | "pageIndex" | "keyword" | "timeFilter" | "sorter" | "displayType">) => {
    if (disableUpdateUrl) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set(tablePrefix + "pageIndex", state.pageIndex.toString());
    if (state.keyword !== undefined && state.keyword !== null) {
      params.set(tablePrefix + "keyword", state.keyword.toString());
    }
    if (filterConfig?.shouldRenderDatePicker && state.timeFilter) {
      params.set("granularity", state.timeFilter.granularity.toString());
      params.set("start_date_time", state.timeFilter.start_date_time.toString());
    }
    if (state.sorter) {
      params.set(tablePrefix + "sorter", JSON.stringify(state.sorter));
    }
    if (state.displayType) {
      params.set(tablePrefix + "displayType", state.displayType.toString());
    }
    state.filters.forEach(filter => {
      params.set(filter.filterKey, filter.value);
    });
    // 用next router替换的话，会引起一次query请求，故直接修改history对象
    const newUrl = "?" + params.toString();
    window.history.replaceState(null, "", newUrl);
  }, [tablePrefix, filterConfig, disableUpdateUrl]);
  const [store] = useState(() => createTableStore({
    ...initStore({
      defaultDisplayType,
      shouldAddTimeFilter: !!filterConfig?.shouldRenderDatePicker,
      searchParams,
      tablePrefix,
      defaultFilter,
      defalutSorter: defalutSorter || [],
      showMonthPicker,
      filterKeyList: filterConfig?.filterGroupList?.map?.(group => group.filterList.map(filter => filter.filterKey)).flat() || []
    }),
    afterUpdate: updateUrl
  }));
  return <StoreContext.Provider value={store} data-sentry-element="unknown" data-sentry-component="TableStoreProvider" data-sentry-source-file="TableStoreProvider.tsx">{children}</StoreContext.Provider>;
};